import { useCallback } from 'react';
import { MapLayers } from 'features/mapping/mapping.constants';
import { User } from 'shared/types';
import { canViewFlightTrackerLayer, hasMembershipBenefits } from 'shared/utils';
import { useMapLayersState } from 'state/useMapLayersState';

export type UseEnableFlightTrackerLayer = {
  enableFlightTrackerLayer: () => void;
  /** No-op unless `user` can view the flight tracker layer. */
  enableFlightTrackerLayerForMember: (user?: User | null) => void;
};

/**
 * The permission is release-flag gated on the backend, so fall back to
 * membership, matching `showMembershipFeatures` and this layer's gate in
 * `canUserAccessMapLayer`.
 */
const canUserViewFlightTrackerLayer = (user: User | null): boolean =>
  canViewFlightTrackerLayer(user) || hasMembershipBenefits(user);

export const useEnableFlightTrackerLayer = (): UseEnableFlightTrackerLayer => {
  const { setMapLayerVisibility } = useMapLayersState();

  const enableFlightTrackerLayer = useCallback((): void => {
    setMapLayerVisibility(MapLayers.FLIGHT_TRACKER, true);
  }, [setMapLayerVisibility]);

  const enableFlightTrackerLayerForMember = useCallback(
    (user?: User | null): void => {
      if (!canUserViewFlightTrackerLayer(user ?? null)) return;
      enableFlightTrackerLayer();
    },
    [enableFlightTrackerLayer],
  );

  return { enableFlightTrackerLayer, enableFlightTrackerLayerForMember };
};
